import React from 'react';
import { AlertTriangle, CheckCircle, Repeat } from 'lucide-react';
import { Card } from '../SubscriptionAppCard';
import type { ContactSubscription } from '../../data/mockContactSubscriptions';
import type { ContactOrderRecord } from '../../data/mockContactOrders';
import { channelGroup } from '../../utils/orderChannels';
import { averageGapDays, daysBetween, formatSpan, latestDate } from '../../utils/orderCadence';
import { formatShortDate } from '../../utils/dateFormat';
interface OrderingCadenceCardProps {
  orders: ContactOrderRecord[];
  subscriptions: ContactSubscription[];
}

// How often the contact orders overall, their active subscriptions, and whether extra purchases are overdue
export function OrderingCadenceCard({
  orders,
  subscriptions
}: OrderingCadenceCardProps) {
  const allDates = orders.map(order => order.date);
  const allGap = averageGapDays(allDates);
  const lastOrder = latestDate(allDates);
  const active = subscriptions.filter(sub => sub.status === 'Active');
  // With an active subscription, only orders placed outside it say whether they're buying on their own
  const hasActive = active.length > 0;
  const watchedDates = hasActive ? orders.filter(order => channelGroup(order.channel) === 'one-time').map(order => order.date) : allDates;
  const watchedGap = averageGapDays(watchedDates);
  const watchedLast = latestDate(watchedDates);
  const daysSince = watchedLast ? daysBetween(watchedLast) : null;
  const daysOverdue = daysSince !== null && watchedGap !== null ? daysSince - watchedGap : null;
  const isOverdue = daysOverdue !== null && daysOverdue > 0;
  const lastLabel = hasActive ? 'Last extra purchase' : 'Last order';
  const indicator = daysSince === null || watchedGap === null ? hasActive ? 'Not enough extra purchases to compare yet' : 'Not enough orders to compare yet' : `${lastLabel} ${formatSpan(daysSince)} ago, usually every ${formatSpan(watchedGap)}, ${isOverdue ? `${formatSpan(daysOverdue as number)} overdue` : 'on track'}`;
  const nextBillings = active.map(sub => sub.nextBilling).filter((date): date is string => Boolean(date)).sort();
  const expectedNext = nextBillings[0] ?? (lastOrder && allGap !== null ? (() => {
    const [y, m, d] = lastOrder.split('-').map(Number);
    const next = new Date(y, m - 1, d + allGap);
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
  })() : null);
  const rows = [{
    label: 'Last order',
    value: lastOrder ? `${formatShortDate(lastOrder)} (${daysBetween(lastOrder)} days ago)` : '—'
  }, {
    label: 'Expected next order',
    value: expectedNext ? formatShortDate(expectedNext) : '—'
  }];
  return <Card padding="none" data-id="element-1644" data-mp="3rfsghus66wj0">
      <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-1645" data-mp="3re7oocx8prou">
        <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-1646" data-mp="2kzq5zamx79py">
          Ordering Cadence
        </h3>
      </div>
      <div className="p-6 bg-white rounded-b-lg" data-id="element-1647" data-mp="2fyiylcoowcjt">
        <div className="flex items-start gap-2" data-id="element-1648" data-mp="25wfatpaomm6s">
          <Repeat className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-1649" data-mp="25xqfdrbybxyy" />
          <span className="text-sm font-medium text-[#0F172A]" data-id="element-1650" data-mp="2avrxa8xn5r8u">
            {allGap !== null ? `Orders roughly every ${formatSpan(allGap)}, across all orders` : 'Not enough orders yet'}
          </span>
        </div>
        <p className="text-sm text-[#475569] mt-2 pl-6" data-id="element-1651" data-mp="2kmcrlk6a89ly">
          {hasActive ? `${active.length} active: ${active.map(sub => sub.cadenceLabel).join(', ')}` : 'No active subscriptions'}
        </p>
        <div className={`mt-4 flex items-start gap-2 rounded-md border px-3 py-2.5 ${isOverdue ? 'bg-[#FEF3C7] border-[#FDE68A]' : 'bg-[#ECFDF5] border-[#A7F3D0]'}`} data-id="element-1652" data-mp="2tjwwd33cso1b">
          {isOverdue ? <AlertTriangle className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-1653" data-mp="33imcaqbovyex" /> : <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" data-id="element-1654" data-mp="14734if24gw6u" />}
          <p className={`text-sm font-medium ${isOverdue ? 'text-[#92400E]' : 'text-[#047857]'}`} data-id="element-1655" data-mp="195mvitvkoy62">
            {indicator}
          </p>
        </div>
        <dl className="mt-4 pt-4 border-t border-[#E2E8F0] space-y-3" data-id="element-1656" data-mp="2zlfe8vi6pex6">
          {rows.map(row => <div key={row.label} className="flex items-center justify-between gap-4" data-id="element-1657" data-mp="2umlz6yn91fuw">
              <dt className="text-sm text-[#475569]" data-id="element-1658" data-mp="38nolnci5os6c">{row.label}</dt>
              <dd className="text-sm font-medium text-[#0F172A] text-right" data-id="element-1659" data-mp="33obwum7q5b14">
                {row.value}
              </dd>
            </div>)}
        </dl>
      </div>
    </Card>;
}